import {FC, ReactNode, useEffect, useState} from 'react';
import {
  Control,
  Controller,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import {useRecoilState, useRecoilValueLoadable} from 'recoil';
import Select from 'react-select';

import {Avatar, Button, ButtonGroup, Chip, Stack, Tooltip, Typography} from '@mui/material';
import {accountGroupUsersSelectionToRegroupState, editGroupModeState} from '../../../store/user-groups/atoms';
import {ModalContainer} from '../../modals/ModalContainer';
import {ConfirmAction} from '../../modals/ConfirmAction';
import {ChooseGroup} from './ChooseGroup';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

import {IWorkerSelectOption} from '../../../api/strategies/searchWorkersStrategy';

import {usersOutOfGroupQueryFamily} from '../../../store/user-groups/selectors';

import {IAccountGroup} from '../../../shared/types/user-account-groups.types';
import {IUserAccount, TypeAccountRoles} from '../../../shared/types/user-account.types';

interface IButtonSwitcher {
  group: IAccountGroup;
  control: Control<any, any>;
  usersToGroup: IWorkerSelectOption<IUserAccount>[];
  preliminaryUsersToGroup: IWorkerSelectOption<IUserAccount>[];
  defaultUsers: IWorkerSelectOption<IUserAccount>[];
  loading: boolean;
  fromPreliminaryToFinal: () => void;
  clearErrors: UseFormClearErrors<any>;
  setValue: UseFormSetValue<any>;
  getValues: UseFormGetValues<any>;
  watch: UseFormWatch<any>;
}

export const ButtonSwitcher: FC<IButtonSwitcher> = ({
  control,
  usersToGroup,
  group,
  preliminaryUsersToGroup,
  loading,
  defaultUsers,
  fromPreliminaryToFinal,
  clearErrors,
  setValue,
  getValues,
  watch,
}) => {
  const [{usersToRegroup}, setUsersToRegroup] = useRecoilState(accountGroupUsersSelectionToRegroupState);
  const [editGroupMode, setEditGroupMode] = useRecoilState(editGroupModeState);
  const [openSearch, setOpenSearch] = useState(false);
  const {contents: usersOutOfGroup, state: usersOutOfGroupState} = useRecoilValueLoadable(
    usersOutOfGroupQueryFamily({role: TypeAccountRoles.WORKER, exGroups: [group.id]}),
  );
  const [openRegroup, setOpenRegroup] = useState(false);

  const preliminaryAnotherGroup = watch('preliminaryAnotherGroup');
  const anotherGroup = watch('anotherGroup');

  const zeroUserToRegroup = !usersToRegroup.length;

  const toggleOpenSearch = () => {
    setOpenSearch((prev) => !prev);
    clearErrors();
  };

  const toggleOpenRegroup = () => {
    setOpenRegroup((prev) => {
      prev && setValue('preliminaryAnotherGroup', null);

      return !prev;
    });
    clearErrors();
  };

  const toggleEditMode = () => {
    setEditGroupMode((prev) => (prev === 'DEFAULT' ? 'REGROUP' : 'DEFAULT'));
    if (editGroupMode === 'REGROUP') {
      setUsersToRegroup({usersToRegroup: [], isLeaderSelected: false});
      setValue('anotherGroup', null);
    }
    clearErrors();
  };

  const handleReady = () => {
    setValue('anotherGroup', preliminaryAnotherGroup);
    toggleOpenRegroup();
  };

  const formatOption: (arg: IWorkerSelectOption<IUserAccount>) => ReactNode = ({value, label, photo}) => (
    <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
      <Avatar sx={{width: 28, height: 28}} src={photo || ''} />
      <Typography variant="subtitle2">{label}</Typography>
    </Stack>
  );

  useEffect(() => {
    zeroUserToRegroup && setValue('anotherGroup', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zeroUserToRegroup]);

  return (
    <>
      {editGroupMode === 'REGROUP' && (
        <Stack
          direction="row"
          justifyContent={anotherGroup ? 'space-between' : 'flex-end'}
          alignItems={anotherGroup ? 'flex-start' : 'normal'}
        >
          {anotherGroup && (
            <Stack direction="column" spacing={0.5}>
              <Stack direction="row" spacing={1}>
                <Tooltip title={group.name}>
                  <Chip sx={{width: 140}} size="small" label={group.name} />
                </Tooltip>
                <Stack spacing={0.25} sx={{position: 'relative'}} alignItems="center" direction="column">
                  <ArrowRightAltIcon />
                  <Typography position="absolute" top="25px" width="100px" textAlign="center" variant="caption">
                    {`${usersToRegroup.length} человек${usersToRegroup.length >= 2 && usersToRegroup.length <= 4 ? 'а' : ''}`}
                  </Typography>
                </Stack>
                <Tooltip title={anotherGroup.value.name}>
                  <Chip sx={{width: 140}} size="small" label={anotherGroup.value.name} />
                </Tooltip>
              </Stack>
            </Stack>
          )}
          <ButtonGroup variant="contained" size="small">
            <Tooltip title="Перенести в другую группу">
              <Button
                onClick={toggleOpenRegroup}
                disabled={usersToRegroup.length < 1 || loading}
                size="small"
                variant="contained"
              >
                Выбрать группу
              </Button>
            </Tooltip>
            <Button onClick={toggleEditMode} disabled={loading} color="error" size="small" variant="contained">
              Отмена
            </Button>
          </ButtonGroup>
        </Stack>
      )}
      {editGroupMode !== 'REGROUP' && (
        <Stack direction="row" sx={{justifyContent: !openSearch ? 'center' : 'normal'}} spacing={2}>
          {!openSearch && (
            <Stack direction="row" spacing={2}>
              <ButtonGroup variant="contained" size="small">
                <Button disabled={loading} onClick={toggleOpenSearch} size="small" variant="contained">
                  Добавить в группу
                </Button>
                <Button
                  onClick={toggleEditMode}
                  disabled={!group.accounts.length || loading}
                  color="success"
                  size="small"
                  variant="contained"
                >
                  Перенести
                </Button>
              </ButtonGroup>
            </Stack>
          )}
          {openSearch && (
            <Stack direction="column" sx={{flex: 1, '& > div': {width: '100%'}}} spacing={1.5}>
              <Controller
                name="preliminaryWorkers"
                control={control}
                render={({field: {value}}) => (
                  <Select
                    autoFocus
                    options={
                      usersOutOfGroupState === 'hasValue'
                        ? usersOutOfGroup.concat(defaultUsers).filter((i: IWorkerSelectOption<IUserAccount>) => {
                            const cond1 = !usersToGroup.find((item) => item.value.id === i.value.id);
                            const cond2 = !preliminaryUsersToGroup.find((item) => item.value.id === i.value.id);

                            return cond1 && cond2;
                          })
                        : []
                    }
                    value={value}
                    isDisabled={loading}
                    isLoading={usersOutOfGroupState === 'loading'}
                    loadingMessage={() => 'Поиск...'}
                    formatOptionLabel={formatOption}
                    noOptionsMessage={() => 'Ничего не найдено'}
                    onChange={(e) => {
                      setValue('preliminaryWorkers', e, {shouldDirty: false});
                    }}
                    isMulti
                    placeholder=" "
                  />
                )}
              />
              <Stack direction="row" sx={{justifyContent: 'flex-end'}}>
                <ButtonGroup variant="contained" size="small">
                  <Button onClick={fromPreliminaryToFinal} disabled={!preliminaryUsersToGroup.length}>
                    Добавить
                  </Button>
                  <Button onClick={toggleOpenSearch} color="error">
                    Отмена
                  </Button>
                </ButtonGroup>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
      {openRegroup && (
        <ModalContainer
          sideEffect={handleReady}
          renderModal={(sideEffect, loading) => (
            <ConfirmAction
              loading={loading}
              open={openRegroup}
              handleClose={toggleOpenRegroup}
              handleSideEffect={sideEffect}
              title="Перенести пользователей"
              buttonTitle="Далее"
              isReady={!!preliminaryAnotherGroup}
            >
              <ChooseGroup
                loading={loading}
                to={preliminaryAnotherGroup}
                control={control}
                from={group}
                setValue={setValue}
              />
            </ConfirmAction>
          )}
        />
      )}
    </>
  );
};
